import { useEffect, useState } from 'react';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { handleAdminError } from '../../common/SharedFunctions';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { formatDateMonthDayYear } from '@apply/ui';
import {
    WatchlistCategoryType,
    WatchlistCategoryTypeOptions,
    WatchlistCureType,
    WatchlistCureTypeOptions,
} from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import LoadPanel from 'devextreme-react/load-panel';

const IssuesPopupTables = ({ watchlistId, loanId, propertyId }) => {
    const [data, set_data] = useState([]);
    const [loadingMessage, set_loadingMessage] = useState('');
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);

    const formatDate = (dt) => {
        if (!dt) {
            return null;
        }
        return formatDateMonthDayYear(dt);
    };

    const onDateRender = (e) => {
        return formatDate(e.value);
    };

    const loadData = async (watchlistId, loanId, propertyId) => {
        try {
            if (loanId) {
                set_loadingMessage('Retrieving issues for Loan...');
                const response = await watchlistApi.getWatchlistLoanIssues(watchlistId, loanId);
                if (response.statusCode !== 200) {
                    throw new Error('Failed to retrieve data!');
                }
                const records = response.result;
                set_data(records);
            } else if (propertyId) {
                set_loadingMessage('Retrieving issues for Property...');
                const response = await watchlistApi.getWatchlistPropertyIssues(watchlistId, propertyId);
                if (response.statusCode !== 200) {
                    throw new Error('Failed to retrieve data!');
                }
                const records = response.result;
                set_data(records);
            }
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve records! Please refresh the page and try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        if (!watchlistId || (!loanId && !propertyId)) {
            return;
        }

        loadData(watchlistId, loanId, propertyId);
    }, [loanId, propertyId, watchlistId]);

    if (!data) {
        return;
    }

    const onCategoryTypeRender = (e) => {
        const catgId = e.data.categoryTypeId;
        if (catgId == WatchlistCategoryType.Other) {
            return e.data.otherCategoryName ?? 'Category Name Not Set';
        }
        var option = WatchlistCategoryTypeOptions.find((a) => a.id === catgId);
        if (!option) {
            return 'Unknown';
        }
        return option.name;
    };

    const onCureTypeRender = (e) => {
        const cureId = e.data.cureTypeId;
        if (cureId == WatchlistCureType.Other) {
            return e.data.otherCureName ?? 'Cure Name Not Set';
        }
        var option = WatchlistCureTypeOptions.find((a) => a.id === cureId);
        if (!option) {
            return null;
        }
        return option.name;
    };

    const onIsOutstandingRender = (e) => {
        var cure = WatchlistCureTypeOptions.find((a) => a.id === e.data.cureTypeId);
        if (!cure) {
            return null;
        }

        if (e.data.isOutstanding) {
            return (
                <span>
                    <i className='green-text'>
                        <FontAwesomeIcon icon={faCheck} />
                    </i>
                </span>
            );
        } else {
            return (
                <span>
                    <i className='red-text'>
                        <FontAwesomeIcon icon={faTimes} />
                    </i>
                </span>
            );
        }
    };

    const unCured = data?.filter((a) => !a.cureTypeId);
    const cured = data?.filter((a) => a.cureTypeId > 0);

    if (!loanId && !propertyId) {
        return null;
    }

    return (
        <>
            <h2 className='decorated'>Uncured</h2>
            <DataGrid
                keyExpr={'categoryTypeId'}
                dataSource={unCured}
                rowAlternationEnabled={true}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                wordWrapEnabled={true}
                noDataText={'No uncured issues found for this loan'}
                loadPanel={true}
            >
                <Column
                    dataField='issueDate'
                    caption='Issue Date'
                    cellRender={onDateRender}
                    width='100'
                />
                <Column
                    dataField='categoryTypeId'
                    caption='Category'
                    cellRender={onCategoryTypeRender}
                    alignment='left'
                />
            </DataGrid>
            <br />
            <h2 className='decorated'>Cured</h2>
            <DataGrid
                keyExpr={'categoryTypeId'}
                dataSource={cured}
                rowAlternationEnabled={true}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                wordWrapEnabled={true}
                noDataText={'No cured issues found for this loan'}
            >
                <Column
                    dataField='curedDate'
                    captiopn='Cured on'
                    cellRender={onDateRender}
                    width='100'
                />
                <Column
                    dataField='categoryTypeId'
                    caption='Category'
                    cellRender={onCategoryTypeRender}
                    alignment='left'
                />
                <Column
                    dataField='cureTypeId'
                    caption='Cure'
                    cellRender={onCureTypeRender}
                    alignment='left'
                />
                <Column
                    dataField='isOutstanding'
                    caption='Outst.'
                    cellRender={onIsOutstandingRender}
                    width='60'
                />
            </DataGrid>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default IssuesPopupTables;
